/**
 * Playfield data model.
 *
 * The tile grid stores only static terrain (Ground/Foliage/Water). Coconuts
 * and spiders are dynamic entities that *sit on* ground tiles, tracked
 * separately. Foliage and water never overlap; spiders/coconuts never sit on
 * foliage or water.
 */

export enum Tile {
  Ground = 0,
  Foliage = 1,
  Water = 2,
}

export interface Position {
  x: number;
  y: number;
}

export interface Coconut {
  x: number;
  y: number;
  /** Invisible coconuts shimmer faintly in-game (~15% of placements). */
  invisible: boolean;
  /** Computed: can the chimp reach this from spawn without crossing hazards? */
  reachable: boolean;
}

export type Axis = 'horizontal' | 'vertical';

export interface Spider {
  axis: Axis;
  /** Top-left of the patrol segment. */
  start: Position;
  /** Number of tiles in the segment (2-4). */
  length: number;
  /** Starting offset within the segment, 0 .. length-1. */
  initialOffset: number;
}

export interface PlayfieldStats {
  foliageTiles: number;
  waterTiles: number;
  spiderTiles: number;
  totalGroundTiles: number;
  /** Ground tiles reachable from spawn (ignoring hazards — pure walkability). */
  connectedGroundTiles: number;
  /** Coconuts reachable from spawn without crossing hazards. */
  coconutsReachable: number;
  generationMs: number;
}

export interface Playfield {
  width: number;
  height: number;
  /** Flat array, index = y * width + x. */
  tiles: Tile[];
  coconuts: Coconut[];
  spiders: Spider[];
  spawn: Position;
  seed: string;
  stats: PlayfieldStats;
}

/** Helper: occupied positions (used during placement to prevent overlap). */
export function tileIdx(width: number, x: number, y: number): number {
  return y * width + x;
}
